import { useAuthStore } from '@/stores/auth.store'
import { API_BASE_URL } from './api-config'

const API_URL = API_BASE_URL

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
    this.name = 'ApiError'
  }
}

async function fetchWithAuth<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const accessToken = useAuthStore.getState().accessToken

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      ...options.headers,
    },
    credentials: 'include',
  })

  const data = await response.json()

  if (!response.ok) {
    throw new ApiError(response.status, data.error || 'Request failed')
  }

  return data
}

export interface InvitationDetails {
  id: string
  parentEmail: string
  childName: string
  childminderEmail: string
  expiresAt: string
}

export interface PendingInvitation {
  id: string
  token: string
  childName: string
  childminderEmail: string
  expiresAt: string
  createdAt: string
}

export interface ChildInvitation {
  id: string
  parentEmail: string
  status: 'pending' | 'accepted' | 'declined' | 'expired'
  createdAt: string
  acceptedAt: string | null
  expiresAt: string
}

export const invitationsApi = {
  // Get invitation details by token (public - no auth required)
  async getInvitation(token: string): Promise<{ invitation: InvitationDetails }> {
    const response = await fetch(`${API_URL}/api/invitations/${token}`)
    const data = await response.json()

    if (!response.ok) {
      throw new ApiError(response.status, data.error || 'Request failed')
    }

    return data
  },

  // Accept invitation (requires auth)
  async acceptInvitation(token: string): Promise<{ message: string; child: { id: string; firstName: string; lastName: string } | null }> {
    return fetchWithAuth<{ message: string; child: { id: string; firstName: string; lastName: string } | null }>(
      `/api/invitations/${token}/accept`,
      { method: 'POST' }
    )
  },

  // Decline invitation (requires auth)
  async declineInvitation(token: string): Promise<{ message: string }> {
    return fetchWithAuth<{ message: string }>(
      `/api/invitations/${token}/decline`,
      { method: 'POST' }
    )
  },

  // Get pending invitations for current user
  async getPendingInvitations(): Promise<{ invitations: PendingInvitation[] }> {
    return fetchWithAuth<{ invitations: PendingInvitation[] }>('/api/invitations/pending/me')
  },

  // Send invitation (childminder only)
  async sendInvitation(childId: string, parentEmail: string): Promise<{
    invitation: { id: string; parentEmail: string; status: string; expiresAt: string; token?: string }
    inviteUrl?: string
  }> {
    return fetchWithAuth('/api/invitations/send', {
      method: 'POST',
      body: JSON.stringify({ childId, parentEmail }),
    })
  },

  // Get invitations for a child (childminder only)
  async getChildInvitations(childId: string): Promise<{ invitations: ChildInvitation[] }> {
    return fetchWithAuth<{ invitations: ChildInvitation[] }>(`/api/invitations/child/${childId}`)
  },

  // Revoke invitation (childminder only)
  async revokeInvitation(invitationId: string): Promise<{ message: string }> {
    return fetchWithAuth<{ message: string }>(`/api/invitations/${invitationId}`, {
      method: 'DELETE',
    })
  },
}

export { ApiError }
